package com.essntl.uikit.components.input.date

import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp
import com.essntl.uikit.components.input.EsInput
import core.dateformat.domain.DateFormat
import core.validation.EsInputState
import kotlin.math.min

@Composable
internal fun EsDateInputCore(
    value: String,
    onValueChange: (String) -> Unit,
    dateFormat: String,
    label: String? = null,
    message: String? = null,
    prefix: @Composable (() -> Unit)? = null,
    suffix: @Composable (() -> Unit)? = null,
    onClear: () -> Unit = {},
    enabled: Boolean = true,
    focusable: Boolean = true,
    readOnly: Boolean = !enabled,
    state: EsInputState = EsInputState.Default,
    visualTransformation: VisualTransformation = VisualTransformation.None,
    keyboardOptions: KeyboardOptions =
        KeyboardOptions.Default.copy(
            keyboardType = KeyboardType.Decimal,
            autoCorrectEnabled = false,
        ),
    keyboardActions: KeyboardActions = KeyboardActions.Default,
    singleLine: Boolean = false,
    maxLines: Int = if (singleLine) 1 else Int.MAX_VALUE,
    minLines: Int = 1,
    interactionSource: MutableInteractionSource = remember { MutableInteractionSource() },
    cursorColor: Color = Color.Unspecified,
    shape: Shape = RoundedCornerShape(12.dp),
    modifier: Modifier = Modifier,
) {
    // Holds the latest internal TextFieldValue state. We need to keep it to have the correct value
    // of the composition.
    var textFieldValueState by remember { mutableStateOf(TextFieldValue(text = value)) }
    // Holds the latest TextFieldValue that BasicTextField was recomposed with. We couldn't simply
    // pass `TextFieldValue(text = value)` to the CoreTextField because we need to preserve the
    // composition.
    val textFieldValue = textFieldValueState.copy(text = value)

    SideEffect {
        if (textFieldValue.selection != textFieldValueState.selection ||
            textFieldValue.composition != textFieldValueState.composition
        ) {
            textFieldValueState = textFieldValue
        }
    }
    // Last String value that either text field was recomposed with or updated in the onValueChange
    // callback. We keep track of it to prevent calling onValueChange(String) for same String when
    // CoreTextField's onValueChange is called multiple times without recomposition in between.
    var lastTextValue by remember(value) {
        mutableStateOf(
            value,
        )
    }

    EsInput(
        value = textFieldValue,
        onClear = onClear,
        focusable = focusable,
        onValueChange = {
            val isMonthFirst = dateFormat == DateFormat.MONTH_DAY.format

            var toAddSelection = 0

            val digitList =
                it.text
                    .let { text ->
                        var newText = text

                        // Handle editing of the text in the middle of the string to give better UX
                        if (
                            textFieldValue.selection.collapsed &&
                            it.selection.collapsed &&
                            it.selection.start > textFieldValue.selection.start &&
                            it.text.length > textFieldValue.text.length &&
                            it.text.length > it.selection.start
                        ) {
                            val addedTextRange = textFieldValue.selection.start..<it.selection.start

                            if (2 in addedTextRange) {
                                toAddSelection++
                            }

                            if (5 in addedTextRange) {
                                toAddSelection++
                            }

                            text
                                .slice(addedTextRange)
                                .forEachIndexed { index, c ->
                                    if (c.isDigit()) {
                                        newText =
                                            newText.removeRange(
                                                startIndex = it.selection.start + index,
                                                endIndex = it.selection.start + index + 1,
                                            )
                                        toAddSelection++
                                    }
                                }
                        }

                        newText
                    }
                    .mapNotNull { c ->
                        if (c.isDigit()) c else null
                    }

            if (digitList.isNotEmpty()) {
                if (isMonthFirst) {
                    if (digitList.first().toString().toInt() > 1)
                        return@EsInput
                } else {
                    if (digitList.first().toString().toInt() > 3)
                        return@EsInput
                }
            }

            if (digitList.size >= 2) {
                val monthsOrDays = digitList.slice(0..<2).joinToString("").toInt()

                if (isMonthFirst) {
                    if (monthsOrDays > 12) {
                        return@EsInput
                    }
                } else {
                    if (monthsOrDays > 31) {
                        return@EsInput
                    }
                }
            }

            if (digitList.size >= 3) {
                if (isMonthFirst) {
                    if (digitList[2].toString().toInt() > 3) {
                        return@EsInput
                    }
                } else {
                    if (digitList[2].toString().toInt() > 1) {
                        return@EsInput
                    }
                }
            }

            if (digitList.size >= 4) {
                val days = digitList.slice(2..<4).joinToString("").toInt()

                if (isMonthFirst) {
                    if (days > 31) {
                        return@EsInput
                    }
                } else {
                    if (days > 12) {
                        return@EsInput
                    }
                }
            }

            val newText =
                digitList
                    .let { list ->
                        val mList =
                            list
                                .slice(0..min(list.lastIndex, 7))
                                .toMutableList()

                        if (mList.size >= 2) mList.add(2, '/')
                        if (mList.size >= 5) mList.add(5, '/')

                        if (
                            it.text.length < lastTextValue.length &&
                            mList.lastOrNull() == '/' &&
                            lastTextValue.endsWith('/')
                        ) {
                            mList.removeAt(mList.lastIndex)
                            mList.removeAt(mList.lastIndex)
                        }

                        mList
                    }
                    .joinToString("")

            val textLengthDiff = newText.length - it.text.length

            var startSelection = it.selection.start + textLengthDiff + toAddSelection
            var endSelection = it.selection.end + textLengthDiff + toAddSelection

            if (startSelection == 2 || startSelection == 5) {
                if (it.selection.min > textFieldValueState.selection.min) {
                    startSelection++
                    endSelection++
                } else {
                    startSelection--
                    endSelection--
                }
            }

            val newSelection =
                TextRange(
                    start = startSelection,
                    end = endSelection,
                )

            val newTextFieldValueState =
                TextFieldValue(
                    text = newText,
                    selection = newSelection,
                )

            textFieldValueState = newTextFieldValueState

            val stringChangedSinceLastInvocation = lastTextValue != newTextFieldValueState.text
            lastTextValue = newTextFieldValueState.text

            if (stringChangedSinceLastInvocation) {
                onValueChange(newTextFieldValueState.text)
            }
        },
        placeholder = dateFormat,
        label = label,
        message = message,
        prefix = prefix,
        suffix = suffix,
        ignoreStateSuffixIcons = true,
        showClearButton = false,
        enabled = enabled,
        readOnly = readOnly,
        state = state,
        visualTransformation = visualTransformation,
        keyboardOptions = keyboardOptions,
        keyboardActions = keyboardActions,
        singleLine = singleLine,
        maxLines = maxLines,
        minLines = minLines,
        interactionSource = interactionSource,
        cursorColor = cursorColor,
        shape = shape,
        modifier = modifier,
    )
}
